/*
  Shade picker: one modal per section, opened by any [data-shade-picker-open].
  The trigger says which kit variant to add and how many shades it needs; the
  modal tracks a quantity per shade and posts a single line item whose
  properties list the chosen shades.
*/
class ShadePickerModal extends HTMLElement {
  constructor() {
    super();
    this.root = this.closest('[data-shade-picker]');
    this.quantities = new Map();
    this.required = 0;
    this.isOpen = false;
    // Set while the modal is showing a bundle that is already in the cart.
    this.cartLine = null;
    this.loadToken = 0;
    this.variantId = null;
    this.lastFocus = null;

    this.onKeydown = this.onKeydown.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.subtitle = this.querySelector('[data-shade-picker-subtitle]');
    this.totalEl = this.querySelector('[data-shade-picker-total]');
    this.submitButton = this.querySelector('[data-shade-picker-submit]');
    this.cards = Array.from(this.querySelectorAll('[data-shade]'));
    this.submitLabel = this.submitButton.textContent.trim();

    this.addEventListener('click', this.onClick);
    this.root.addEventListener('click', (event) => {
      if (event.target.closest('[data-shade-picker-close]')) this.close();
    });
  }

  /* ---------- open / close ---------- */

  open(trigger) {
    this.lastFocus = trigger || document.activeElement;
    this.required = Number(trigger?.dataset.shadeCount) || 0;
    this.variantId = trigger?.dataset.variantId || null;

    if (this.subtitle) this.subtitle.textContent = trigger?.dataset.subtitle || '';

    this.reset();
    this.loadFromCart();

    this.isOpen = true;
    this.root.hidden = false;
    this.root.setAttribute('aria-hidden', 'false');
    // Next frame, so the transition runs from the hidden state. Re-check the
    // flag: a close can land before the frame does.
    requestAnimationFrame(() => {
      if (this.isOpen) this.root.classList.add('is-open');
    });
    document.body.classList.add('shade-picker-open');
    document.addEventListener('keydown', this.onKeydown);

    const close = this.querySelector('[data-shade-picker-close]');
    if (close) close.focus();
  }

  close() {
    this.isOpen = false;
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('shade-picker-open');
    document.removeEventListener('keydown', this.onKeydown);

    const done = () => {
      if (!this.isOpen) this.root.hidden = true;
    };
    this.root.addEventListener('transitionend', done, { once: true });
    // Fallback when the modal is not animated (reduced motion, older browsers).
    setTimeout(done, 400);

    if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  /* ---------- existing cart line ---------- */

  /*
    The cart is the source of truth: if this kit is already in it, the modal
    opens showing those shades so the customer edits their bundle instead of
    silently adding a second one.
  */
  loadFromCart() {
    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const token = (this.loadToken += 1);

    fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        // A close-and-reopen while this was in flight makes the answer stale.
        if (token !== this.loadToken || !this.isOpen) return;

        const line = (cart.items || []).find(
          (item) => String(item.variant_id) === String(this.variantId)
        );
        if (!line) return;

        this.cartLine = line;
        this.applyLine(line);
      })
      .catch(() => {
        /* No cart, no prefill — the modal still works as an empty bundle. */
      });
  }

  applyLine(line) {
    const properties = line.properties || {};
    // Hidden handle list first: titles change, handles rarely do.
    const handles = properties._shades
      ? String(properties._shades).split(',').filter(Boolean)
      : Object.keys(properties)
          .filter((key) => /^Shade \d+$/.test(key))
          .map((key) => this.handleForTitle(properties[key]))
          .filter(Boolean);

    handles.forEach((handle) => {
      const card = this.cards.find((item) => item.dataset.shadeHandle === handle);
      if (!card) return;
      if (this.required && this.total >= this.required) return;
      this.quantities.set(handle, (this.quantities.get(handle) || 0) + 1);
    });

    this.cards.forEach((card) => {
      this.renderCard(card, this.quantities.get(card.dataset.shadeHandle) || 0);
    });
    this.renderTotal();
  }

  handleForTitle(title) {
    const card = this.cards.find((item) => item.dataset.shadeTitle === title);
    return card ? card.dataset.shadeHandle : null;
  }

  /* ---------- selection ---------- */

  reset() {
    this.cartLine = null;
    this.quantities.clear();
    this.cards.forEach((card) => this.renderCard(card, 0));
    this.renderTotal();
  }

  get total() {
    let sum = 0;
    this.quantities.forEach((qty) => {
      sum += qty;
    });
    return sum;
  }

  onClick(event) {
    if (event.target.closest('[data-shade-picker-submit]')) {
      this.submit();
      return;
    }

    const card = event.target.closest('[data-shade]');
    if (!card) return;

    if (event.target.closest('[data-shade-add]') || event.target.closest('[data-shade-increase]')) {
      this.change(card, 1);
    } else if (event.target.closest('[data-shade-decrease]')) {
      this.change(card, -1);
    }
  }

  change(card, delta) {
    const key = card.dataset.shadeHandle;
    const current = this.quantities.get(key) || 0;
    const next = current + delta;

    if (next < 0) return;
    if (delta > 0 && this.required && this.total >= this.required) return;

    if (next === 0) {
      this.quantities.delete(key);
    } else {
      this.quantities.set(key, next);
    }

    this.renderCard(card, next);
    this.renderTotal();
  }

  renderCard(card, qty) {
    const addButton = card.querySelector('[data-shade-add]');
    const stepper = card.querySelector('[data-shade-stepper]');
    const qtyEl = card.querySelector('[data-shade-qty]');

    if (qtyEl) qtyEl.textContent = qty;
    card.classList.toggle('shade-card--selected', qty > 0);
    if (addButton) addButton.hidden = qty > 0;
    if (stepper) stepper.hidden = qty === 0;
  }

  renderTotal() {
    const total = this.total;
    const complete = this.required > 0 && total === this.required;

    if (this.totalEl) this.totalEl.textContent = this.required ? `${total}/${this.required}` : total;
    if (this.submitButton) {
      this.submitButton.disabled = !complete;
      this.submitButton.textContent = this.cartLine
        ? this.submitButton.dataset.updateLabel || 'Update Bundle'
        : this.submitLabel;
    }

    // At the limit, every increase is a no-op — say so rather than letting it fail silently.
    const atLimit = this.required > 0 && total >= this.required;
    this.cards.forEach((card) => {
      const increase = card.querySelector('[data-shade-increase]');
      const add = card.querySelector('[data-shade-add]');
      if (increase) increase.disabled = atLimit;
      if (add) add.disabled = atLimit;
    });
  }

  /* ---------- add to cart ---------- */

  submit() {
    if (!this.variantId || this.submitButton.disabled) return;

    const properties = {};
    const handles = [];
    let index = 0;
    this.cards.forEach((card) => {
      const qty = this.quantities.get(card.dataset.shadeHandle) || 0;
      for (let i = 0; i < qty; i += 1) {
        index += 1;
        properties[`Shade ${index}`] = card.dataset.shadeTitle;
        handles.push(card.dataset.shadeHandle);
      }
    });
    // Underscore keys stay hidden from the customer; this one survives renames
    // so reopening the modal can restore the exact selection.
    properties._shades = handles.join(',');

    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const restore = this.submitButton.textContent;
    this.submitButton.disabled = true;
    this.submitButton.textContent = this.submitButton.dataset.busyLabel || 'Adding…';

    const drawer = document.querySelector('cart-drawer');
    // Editing the line already in the cart rather than stacking a second kit.
    const editing = Boolean(this.cartLine);
    const endpoint = editing ? 'cart/change.js' : 'cart/add.js';
    const body = editing
      ? { id: this.cartLine.key, quantity: this.cartLine.quantity, properties }
      : { items: [{ id: Number(this.variantId), quantity: 1, properties }] };

    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
    }

    fetch(`${rootUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then((response) => response.json())
      .then((json) => {
        if (json.status) throw new Error(json.description || json.message);

        this.submitButton.textContent = restore;
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        // add.js answers with the added items, change.js with the whole cart;
        // either way the rendered sections can sit at the top level or on the
        // first item.
        const first = Array.isArray(json.items) ? json.items[0] : null;
        const payload = json.sections ? json : (first && first.sections ? first : null);

        if (drawer && typeof drawer.renderContents === 'function' && payload) {
          drawer.renderContents(payload);
        } else {
          window.location.href = `${rootUrl}cart`;
          return;
        }
        this.close();
      })
      .catch(() => {
        this.submitButton.textContent = restore;
        this.submitButton.disabled = false;
        window.location.href = `${rootUrl}cart`;
      });
  }
}

customElements.define('shade-picker-modal', ShadePickerModal);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-shade-picker-open]');
  if (!trigger) return;

  const picker = document.getElementById(`ShadePicker-${trigger.dataset.picker}`);
  const modal = picker && picker.querySelector('shade-picker-modal');
  if (!modal) return;

  event.preventDefault();
  modal.open(trigger);
});
